/**
 * HabitsModule - Page component for the Habit Tracker module.
 *
 * Loads habit data on mount (lazy hydration — data is only fetched when this
 * module activates via React.lazy). Renders the habit list with daily summary,
 * habit detail view, and creation form.
 *
 * @see Requirements 8.1, 8.2, 8.3, 8.9, 13.3
 */

import { useState, useCallback, useMemo } from 'react';
import { useHabitStore } from '@/stores';
import { HabitList } from '@/components/habits/HabitList';
import { HabitDetail } from '@/components/habits/HabitDetail';
import { HabitForm } from '@/components/habits/HabitForm';
import { FloatingActionButton } from '@/components/ui/FloatingActionButton';

export default function HabitsModule() {
  const [showForm, setShowForm] = useState(false);
  const [selectedHabitId, setSelectedHabitId] = useState<string | null>(null);
  const habits = useHabitStore((s) => s.habits);

  // Memoized: find the selected habit object from the store
  const selectedHabit = useMemo(
    () => (selectedHabitId ? habits.find((h) => h.habitId === selectedHabitId) ?? null : null),
    [habits, selectedHabitId]
  );

  const handleCreateHabit = useCallback(() => {
    setShowForm(true);
    setSelectedHabitId(null);
  }, []);

  const handleHabitClick = useCallback((habitId: string) => {
    setSelectedHabitId(habitId);
    setShowForm(false);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedHabitId(null);
  }, []);

  return (
    <div className="relative flex flex-col h-full p-6 gap-4">
      {/* Header */}
      <h1 className="text-heading-1 font-extrabold text-white tracking-tight">Habits</h1>

      {/* Main content */}
      <div className="flex-1 min-h-0 flex gap-4">
        {/* Habit list (loads data on mount via internal useEffect) */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <HabitList
            onCreateHabit={handleCreateHabit}
            onHabitClick={handleHabitClick}
          />
        </div>

        {/* Side panel: creation form or detail view */}
        {(showForm || selectedHabit) && (
          <div className="w-[360px] flex-shrink-0 card-gradient overflow-y-auto transition-all duration-200 ease-in-out p-4">
            {showForm && <HabitForm onCancel={handleCloseForm} />}
            {selectedHabit && !showForm && (
              <HabitDetail habit={selectedHabit} onClose={handleCloseDetail} />
            )}
          </div>
        )}
      </div>

      {/* Floating Action Button - shows form */}
      <FloatingActionButton
        onClick={handleCreateHabit}
        label="+ New Habit"
        gradient="emerald"
      />
    </div>
  );
}
